/**
 * Hybrid video compressor:
 *   1. WebCodecs API (hardware GPU acceleration, 5-20x faster)
 *   2. FFmpeg.wasm fallback (software, works everywhere)
 *
 * Mirrors the Flutter app's file_compress.dart logic:
 * - Max 180s duration
 * - Smart skip: H.264, ≤720p, ≤2500kbps → upload raw
 * - Compress: H.264, 2Mbps, 720p cap, AAC 96k
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
// WebCodecs loaded dynamically to avoid crashes on unsupported mobile browsers
// import { isWebCodecsSupported, compressWithWebCodecs } from './webCodecsCompressor';

const MAX_DURATION_SECONDS = 180; // 3 minutes
const MAX_FILE_SIZE_MB = 500; // Max raw file size before compression

/**
 * INSTANT validation — uses native HTML5 <video> element.
 * No FFmpeg needed. Checks duration + file size in milliseconds.
 * Call this ON FILE SELECT, before any compression.
 */
export function quickValidateVideo(file) {
    return new Promise((resolve) => {
        const fileSizeMB = file.size / (1024 * 1024);

        if (fileSizeMB > MAX_FILE_SIZE_MB) {
            resolve({
                valid: false,
                error: `Video is too large (${fileSizeMB.toFixed(0)} MB). Maximum raw size is ${MAX_FILE_SIZE_MB} MB.`,
                durationSeconds: 0,
                fileSizeMB,
            });
            return;
        }

        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
            URL.revokeObjectURL(video.src);
            const duration = video.duration;

            if (duration > MAX_DURATION_SECONDS) {
                const maxMin = Math.floor(MAX_DURATION_SECONDS / 60);
                resolve({
                    valid: false,
                    error: `Video is too long (${Math.floor(duration / 60)}:${String(Math.floor(duration % 60)).padStart(2, '0')}). Maximum is ${maxMin} minutes.`,
                    durationSeconds: duration,
                    fileSizeMB,
                });
            } else {
                resolve({ valid: true, durationSeconds: duration, fileSizeMB });
            }
        };

        video.onerror = () => {
            URL.revokeObjectURL(video.src);
            resolve({ valid: true, durationSeconds: 0, fileSizeMB });
        };

        setTimeout(() => {
            URL.revokeObjectURL(video.src);
            resolve({ valid: true, durationSeconds: 0, fileSizeMB });
        }, 3000);

        video.src = URL.createObjectURL(file);
    });
}

/**
 * Quick probe using HTML5 <video> element — no FFmpeg required.
 * Gets duration, dimensions, and estimates if smart skip is possible.
 */
function quickProbeVideo(file) {
    return new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
            URL.revokeObjectURL(video.src);
            const fileSizeMB = file.size / (1024 * 1024);
            const bitrateKbps = video.duration > 0
                ? Math.round((file.size * 8) / (video.duration * 1000))
                : 0;

            resolve({
                durationSeconds: video.duration,
                width: video.videoWidth,
                height: video.videoHeight,
                bitrateKbps,
                fileSizeMB,
            });
        };

        video.onerror = () => {
            URL.revokeObjectURL(video.src);
            resolve(null); // Can't probe natively
        };

        setTimeout(() => {
            URL.revokeObjectURL(video.src);
            resolve(null);
        }, 3000);

        video.src = URL.createObjectURL(file);
    });
}

/**
 * Check if video is already optimal (no re-encoding needed).
 * Note: We can't detect codec from HTML5 video, so we use
 * file size heuristic: if file is ≤ 20MB and resolution ≤ 720p, skip.
 */
function isLikelyOptimal(probe) {
    if (!probe) return false;
    const shortestSide = Math.min(probe.width, probe.height);
    const isSmallRes = shortestSide <= 720;
    const isLowBitrate = probe.bitrateKbps > 0 && probe.bitrateKbps <= 1500;
    const isSmallFile = probe.fileSizeMB <= 20;

    return isSmallRes && (isLowBitrate || isSmallFile);
}

// ─── FFmpeg.wasm (software fallback) ───

let ffmpegInstance = null;
let ffmpegLoading = false;
let ffmpegLoaded = false;

async function getFFmpeg(onLog) {
    if (ffmpegInstance && ffmpegLoaded) return ffmpegInstance;
    if (ffmpegLoading) {
        while (ffmpegLoading) {
            await new Promise(r => setTimeout(r, 100));
        }
        return ffmpegInstance;
    }

    ffmpegLoading = true;
    try {
        const ffmpeg = new FFmpeg();
        if (onLog) {
            ffmpeg.on('log', ({ message }) => onLog(message));
        }

        await ffmpeg.load({
            coreURL: 'https://unpkg.com/@ffmpeg/core@0.12.10/dist/umd/ffmpeg-core.js',
        });

        ffmpegInstance = ffmpeg;
        ffmpegLoaded = true;
        return ffmpeg;
    } finally {
        ffmpegLoading = false;
    }
}

/**
 * Compress with FFmpeg.wasm (software, slower)
 */
async function compressWithFFmpeg(file, onProgress, onLog, durationHint) {
    const ffmpeg = await getFFmpeg(onLog);

    const inputExt = file.name.substring(file.name.lastIndexOf('.'));
    const inputName = 'input' + inputExt;
    const outputName = 'output.mp4';

    if (onLog) onLog('Loading file into FFmpeg...');
    await ffmpeg.writeFile(inputName, await fetchFile(file));

    // Get duration if not provided
    let duration = durationHint || 0;
    if (!duration) {
        let logOutput = '';
        const logHandler = ({ message }) => { logOutput += message + '\n'; };
        ffmpeg.on('log', logHandler);
        try { await ffmpeg.exec(['-i', inputName, '-hide_banner']); } catch (_) {}
        ffmpeg.off('log', logHandler);
        const match = logOutput.match(/Duration:\s*(\d+):(\d+):(\d+)\.(\d+)/);
        if (match) {
            duration = parseInt(match[1]) * 3600 + parseInt(match[2]) * 60
                + parseInt(match[3]) + parseInt(match[4]) / 100;
        }
    }

    // Progress tracking
    let lastProgress = 0;
    if (duration > 0) {
        ffmpeg.on('progress', ({ time }) => {
            const timeSec = time / 1000000;
            const p = Math.min(timeSec / duration, 0.99);
            if (onProgress && p > lastProgress) {
                lastProgress = p;
                onProgress(p);
            }
        });
    }

    if (onLog) onLog('Compressing with FFmpeg (software)...');

    try {
        await ffmpeg.exec([
            '-i', inputName,
            '-c:v', 'libx264',
            '-preset', 'medium',
            '-crf', '30',
            '-maxrate', '1000k',
            '-bufsize', '2000k',
            '-vf', "scale='if(gt(iw,ih),min(1280,iw),-2)':'if(gt(iw,ih),-2,min(720,ih))'",
            '-c:a', 'aac',
            '-b:a', '64k',
            '-movflags', '+faststart',
            '-y', outputName,
        ]);

        const data = await ffmpeg.readFile(outputName);
        const compressedBlob = new Blob([data.buffer], { type: 'video/mp4' });
        const compressedFile = new File([compressedBlob], file.name.replace(/\.\w+$/, '.mp4'), {
            type: 'video/mp4',
        });

        await ffmpeg.deleteFile(inputName);
        await ffmpeg.deleteFile(outputName);

        if (onProgress) onProgress(1);
        if (onLog) onLog(`FFmpeg done: ${formatSize(file.size)} → ${formatSize(compressedFile.size)}`);

        return {
            file: compressedFile,
            skipped: false,
            originalSize: file.size,
            compressedSize: compressedFile.size,
            method: 'ffmpeg',
        };
    } catch (err) {
        try { await ffmpeg.deleteFile(inputName); } catch (_) {}
        try { await ffmpeg.deleteFile(outputName); } catch (_) {}

        if (onLog) onLog(`FFmpeg failed: ${err.message}. Using original.`);
        if (onProgress) onProgress(1);

        return {
            file: file,
            skipped: true,
            originalSize: file.size,
            compressedSize: file.size,
            method: 'none',
        };
    }
}

// ─── Main API ───

/**
 * Compress a video file.
 * Tries WebCodecs (GPU) first, falls back to FFmpeg.wasm (software).
 *
 * @param {File} file - Input video file
 * @param {Function} onProgress - Progress callback (0.0 - 1.0)
 * @param {Function} onLog - Optional log callback
 * @returns {{ file: File, skipped: boolean, originalSize: number, compressedSize: number, method: string }}
 */
export async function compressVideo(file, onProgress, onLog) {
    if (onProgress) onProgress(0);

    // 1. Quick probe (instant, no FFmpeg)
    const probe = await quickProbeVideo(file);

    if (probe) {
        if (onLog) onLog(`Probe: ${probe.width}x${probe.height} ${probe.bitrateKbps}kbps ${probe.durationSeconds.toFixed(1)}s ${probe.fileSizeMB.toFixed(1)}MB`);

        // 2. Duration check (already validated on select, but double-check)
        if (probe.durationSeconds > MAX_DURATION_SECONDS) {
            throw new VideoTooLongError(probe.durationSeconds, MAX_DURATION_SECONDS,
                `Video is too long (${Math.floor(probe.durationSeconds)}s). Maximum is ${Math.floor(MAX_DURATION_SECONDS / 60)} minutes.`);
        }

        // 3. Smart skip
        if (isLikelyOptimal(probe)) {
            if (onLog) onLog('SKIP — already small & low-res, no compression needed');
            if (onProgress) onProgress(1);
            return {
                file: file,
                skipped: true,
                originalSize: file.size,
                compressedSize: file.size,
                method: 'skip',
            };
        }
    }

    // 4. Try WebCodecs (hardware accelerated, 5-20x faster)
    // Only use on desktop Chrome/Edge — mobile Safari/Chrome WebCodecs is unreliable
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const hasWebCodecs = typeof VideoEncoder !== 'undefined' && typeof VideoDecoder !== 'undefined';

    if (hasWebCodecs && !isMobile) {
        if (onLog) onLog('Using WebCodecs (hardware accelerated)...');
        try {
            const { compressWithWebCodecs } = await import('./webCodecsCompressor');
            const result = await compressWithWebCodecs(file, onProgress);
            if (onLog) onLog(`WebCodecs done: ${formatSize(file.size)} → ${formatSize(result.compressedSize)}`);
            return result;
        } catch (e) {
            console.warn('WebCodecs failed:', e);
            if (onLog) onLog(`WebCodecs failed: ${e.message}. Falling back to FFmpeg...`);
            if (onProgress) onProgress(0);
        }
    } else {
        if (onLog) onLog(isMobile ? 'Mobile detected, using FFmpeg...' : 'WebCodecs not available, using FFmpeg...');
    }

    // 5. Fallback: FFmpeg.wasm (software)
    return await compressWithFFmpeg(file, onProgress, onLog, probe?.durationSeconds);
}

/**
 * Format bytes to human-readable string
 */
export function formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export class VideoTooLongError extends Error {
    constructor(actualSeconds, maxSeconds, message) {
        super(message);
        this.name = 'VideoTooLongError';
        this.actualSeconds = actualSeconds;
        this.maxSeconds = maxSeconds;
    }
}

